/**
 * Связка двух Swiper + фильтр типа материала + видео в карточках.
 * На мобилке (≤781px) слайдер отключён: 2 карточки + «Загрузить еще» по 2.
 */
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

const WMF_MOBILE_MAX = 781;
const WMF_MOBILE_INITIAL = 2;
const WMF_MOBILE_STEP = 2;

/**
 * @param {string | undefined} raw
 * @return {Record<string, unknown> | null}
 */
function parseConfig(raw) {
	if (!raw) {
		return null;
	}
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function wmfViewportWidth() {
	return typeof window !== 'undefined' ? window.innerWidth : 1025;
}

function isWmfMobileViewport() {
	return wmfViewportWidth() <= WMF_MOBILE_MAX;
}

/**
 * @param {number} slideTotal
 * @param {Record<string, unknown>} cfg
 * @return {number}
 */
function wmfLogicalThumbSpv(slideTotal, cfg) {
	const vw = wmfViewportWidth();
	const capSlides = Math.max(1, slideTotal - 1);
	const tablet = typeof cfg.thumbsTablet === 'number' ? cfg.thumbsTablet : 2;
	const desktop = typeof cfg.thumbsDesktop === 'number' ? cfg.thumbsDesktop : 2;
	if (vw <= 781) {
		return 1;
	}
	if (vw <= 1200) {
		return Math.min(tablet, capSlides);
	}
	return Math.min(desktop, capSlides);
}

/**
 * @param {number} slideTotal
 * @param {Record<string, unknown>} cfg
 * @return {boolean}
 */
function wmfThumbsNeedNav(slideTotal, cfg) {
	const isMobile = wmfViewportWidth() <= 781;
	const visibleSlides = isMobile ? slideTotal : Math.max(1, slideTotal - 1);
	return visibleSlides > wmfLogicalThumbSpv(slideTotal, cfg);
}

/**
 * @param {number} slideTotal
 * @param {number} spv
 * @return {number}
 */
function getMinThumbIndex(slideTotal, spv) {
	if (wmfViewportWidth() <= 781) {
		return 0;
	}
	if (slideTotal < 2) {
		return 0;
	}
	if (slideTotal === 2) {
		return 1;
	}
	return slideTotal > spv ? 1 : 0;
}

/**
 * @param {Swiper} thumbs
 * @param {Swiper} main
 * @param {number} speed
 */
function syncMainFromThumbs(thumbs, main, speed = 0) {
	if (wmfViewportWidth() <= 781) {
		return;
	}
	if (!thumbs?.slides || !main?.slides || thumbs.destroyed || main.destroyed) {
		return;
	}
	let m = thumbs.activeIndex - 1;
	if (m < 0) {
		m = 0;
	}
	const maxM = Math.max(0, main.slides.length - 1);
	if (m > maxM) {
		m = maxM;
	}
	if (main.activeIndex !== m) {
		main.slideTo(m, speed);
	}
}

/**
 * @param {Swiper} thumbs
 * @return {number}
 */
function getWmfTransitionMs(thumbs) {
	const s = thumbs.params.speed;
	return typeof s === 'number' && s >= 0 ? s : 400;
}

/**
 * @param {Swiper} thumbs
 * @param {HTMLElement | null} root
 * @param {string} sliderId
 * @param {boolean} enableNav
 */
function updateWmfNavButtons(thumbs, root, sliderId, enableNav, slideTotal, cfg) {
	if (!enableNav || !root || !thumbs?.slides || thumbs.destroyed) {
		return;
	}
	const prevBtn = root.querySelector(`[data-wmf-prev="${sliderId}"]`);
	const nextBtn = root.querySelector(`[data-wmf-next="${sliderId}"]`);
	if (!prevBtn || !nextBtn) {
		return;
	}
	if (!wmfThumbsNeedNav(slideTotal, cfg)) {
		prevBtn.classList.add('swiper-button-disabled');
		nextBtn.classList.add('swiper-button-disabled');
		prevBtn.setAttribute('disabled', '');
		nextBtn.setAttribute('disabled', '');
		return;
	}
	const spv = wmfLogicalThumbSpv(slideTotal, cfg);
	const minT = getMinThumbIndex(thumbs.slides.length, spv);
	const atMin = thumbs.activeIndex <= minT;
	const atMax = thumbs.isEnd;

	prevBtn.classList.toggle('swiper-button-disabled', atMin);
	nextBtn.classList.toggle('swiper-button-disabled', atMax);
	prevBtn.toggleAttribute('disabled', atMin);
	nextBtn.toggleAttribute('disabled', atMax);
}

/**
 * @param {HTMLElement | null} root
 * @param {Swiper} thumbs
 * @param {boolean} enableNav
 */
function toggleNavLocked(root, thumbs, enableNav, slideTotal, cfg) {
	if (!enableNav || !root) {
		return;
	}
	const hideNav = !wmfThumbsNeedNav(slideTotal, cfg) || Boolean(thumbs?.isLocked);
	root.classList.toggle('wp-block-wps-blocks-materials-feed--nav-locked', hideNav);
}

/**
 * @param {HTMLElement} pairEl
 */
function tearDownWmfSwiper(pairEl) {
	const s = pairEl._wmfSwipers;
	try {
		s?.main?.destroy?.(true, true);
		s?.thumbs?.destroy?.(true, true);
	} catch {
		// Swiper уже уничтожен.
	}
	pairEl._wmfSwipers = null;
}

/**
 * @param {HTMLElement} pairEl
 * @param {HTMLElement} root
 */
function teardownWmfMobile(pairEl, root) {
	root.classList.remove('wp-block-wps-blocks-materials-feed--is-mobile-list');
	pairEl.querySelectorAll('.swiper-slide.is-wmf-mobile-hidden').forEach((el) => el.classList.remove('is-wmf-mobile-hidden'));
	pairEl._wmfMobileVisible = undefined;
}

/**
 * @param {HTMLElement} pairEl
 * @param {HTMLElement} root
 */
function initWmfMobileList(pairEl, root) {
	const cfg = parseConfig(pairEl.dataset.wmfConfig);
	if (!cfg || typeof cfg.sliderId !== 'string') {
		return;
	}
	tearDownWmfSwiper(pairEl);
	root.classList.add('wp-block-wps-blocks-materials-feed--is-mobile-list');

	const thumbsEl = pairEl.querySelector(`[data-wmf-thumbs="${cfg.sliderId}"]`);
	const slides = thumbsEl ? [...thumbsEl.querySelectorAll('.swiper-slide')] : [];
	const total = slides.length;
	let visible = Math.min(WMF_MOBILE_INITIAL, total);
	pairEl._wmfMobileVisible = visible;

	const btn = root.querySelector('[data-wmf-load-more]');
	const apply = () => {
		slides.forEach((slide, i) => {
			slide.classList.toggle('is-wmf-mobile-hidden', i >= visible);
		});
		if (btn instanceof HTMLButtonElement) {
			btn.hidden = total <= WMF_MOBILE_INITIAL || visible >= total;
		}
	};
	apply();
}

/**
 * @param {HTMLElement} root
 */
function syncWmfBlockLayout(root) {
	const pairEl = root.querySelector('[data-wmf-config]');
	if (!(pairEl instanceof HTMLElement)) {
		return;
	}
	if (isWmfMobileViewport()) {
		teardownWmfMobile(pairEl, root);
		tearDownWmfSwiper(pairEl);
		initWmfMobileList(pairEl, root);
	} else {
		teardownWmfMobile(pairEl, root);
		tearDownWmfSwiper(pairEl);
		const btn = root.querySelector('[data-wmf-load-more]');
		if (btn instanceof HTMLButtonElement) {
			btn.hidden = true;
		}
		initWmfDesktopSliders(pairEl);
	}
}

/**
 * @param {HTMLElement} pairEl
 * @param {HTMLElement} mainEl
 * @param {HTMLElement} thumbsEl
 */
function cacheWmfSlideRows(pairEl, mainEl, thumbsEl) {
	if (pairEl._wmfSlideRows) {
		return;
	}
	const mains = [...mainEl.querySelectorAll('.swiper-wrapper > .swiper-slide')];
	const thumbs = [...thumbsEl.querySelectorAll('.swiper-wrapper > .swiper-slide')];
	pairEl._wmfSlideRows = mains.map((main, i) => ({
		main,
		thumb: thumbs[i],
		kind: main.getAttribute('data-wmf-kind') || 'article',
	}));
}

/**
 * @param {HTMLElement} root
 */
function showWmfEmpty(root) {
	let el = root.querySelector('.wp-block-wps-blocks-materials-feed__empty');
	if (!el) {
		el = document.createElement('p');
		el.className = 'wp-block-wps-blocks-materials-feed__empty';
		el.textContent = root.getAttribute('data-wmf-empty-label') || '';
		const anchor = root.querySelector('[data-wmf-config]') || root.querySelector('.wp-block-wps-blocks-materials-feed__pair');
		if (anchor) {
			anchor.insertAdjacentElement('afterend', el);
		} else {
			root.appendChild(el);
		}
	}
	el.hidden = false;
}

/**
 * @param {HTMLElement} root
 */
function hideWmfEmpty(root) {
	const el = root.querySelector('.wp-block-wps-blocks-materials-feed__empty');
	if (el) {
		el.hidden = true;
	}
}

/**
 * @param {HTMLElement} root
 * @param {string} filter
 */
function updateFilterTabs(root, filter) {
	root.querySelectorAll('.wp-block-wps-blocks-materials-feed__filter[data-wmf-filter]').forEach((btn) => {
		if (!(btn instanceof HTMLButtonElement)) {
			return;
		}
		const isOn = btn.getAttribute('data-wmf-filter') === filter;
		btn.classList.toggle('is-active', isOn);
		btn.setAttribute('aria-selected', isOn ? 'true' : 'false');
	});
}

/**
 * @param {HTMLElement} root
 * @param {string} filter
 */
function applyFilterToSingle(root, filter) {
	const card = root.querySelector('.wp-block-wps-blocks-materials-feed__single-card');
	if (!card) {
		return false;
	}
	const kind = card.getAttribute('data-wmf-kind') || 'article';
	const hide = filter !== 'all' && kind !== filter;
	card.classList.toggle('is-wmf-hidden', hide);
	const pair = card.closest('.wp-block-wps-blocks-materials-feed__pair');
	const secondary = pair?.querySelector('.wp-block-wps-blocks-materials-feed__secondary');
	if (secondary) {
		secondary.classList.toggle('is-wmf-hidden', hide);
	}
	if (hide) {
		showWmfEmpty(root);
	} else {
		hideWmfEmpty(root);
	}
	return true;
}

/**
 * @param {HTMLElement} pairEl
 * @param {HTMLElement} root
 * @param {string} filter
 */
function rebuildWmfSliders(pairEl, root, filter) {
	wmfPauseVideos(root);
	const cfg0 = parseConfig(pairEl.dataset.wmfConfig);
	if (!cfg0 || typeof cfg0.sliderId !== 'string') {
		return;
	}
	const mainEl = pairEl.querySelector(`[data-wmf-main="${cfg0.sliderId}"]`);
	const thumbsEl = pairEl.querySelector(`[data-wmf-thumbs="${cfg0.sliderId}"]`);
	if (!mainEl || !thumbsEl) {
		return;
	}

	cacheWmfSlideRows(pairEl, mainEl, thumbsEl);
	tearDownWmfSwiper(pairEl);
	teardownWmfMobile(pairEl, root);

	const mw = mainEl.querySelector('.swiper-wrapper');
	const tw = thumbsEl.querySelector('.swiper-wrapper');
	if (!mw || !tw) {
		return;
	}
	mw.innerHTML = '';
	tw.innerHTML = '';

	let n = 0;
	const rows = pairEl._wmfSlideRows;
	if (!Array.isArray(rows)) {
		return;
	}
	for (const row of rows) {
		if (filter === 'all' || row.kind === filter) {
			mw.appendChild(row.main);
			tw.appendChild(row.thumb);
			n++;
		}
	}

	root.classList.toggle('wp-block-wps-blocks-materials-feed--pair-only', n === 2);

	const cfgNext = { ...cfg0, enableNav: n >= 2, totalSlides: n };
	pairEl.dataset.wmfConfig = JSON.stringify(cfgNext);

	if (n === 0) {
		pairEl.hidden = true;
		showWmfEmpty(root);
		return;
	}

	pairEl.hidden = false;
	hideWmfEmpty(root);

	syncWmfBlockLayout(root);
}

/**
 * @param {HTMLElement} pairEl
 */
function initWmfDesktopSliders(pairEl) {
	const cfg = parseConfig(pairEl.dataset.wmfConfig);
	if (!cfg || typeof cfg.sliderId !== 'string') {
		return;
	}

	tearDownWmfSwiper(pairEl);

	const root = pairEl.closest('.wp-block-wps-blocks-materials-feed');
	const mainEl = pairEl.querySelector(`[data-wmf-main="${cfg.sliderId}"]`);
	const thumbsEl = pairEl.querySelector(`[data-wmf-thumbs="${cfg.sliderId}"]`);
	if (!mainEl || !thumbsEl) {
		return;
	}

	const enableNav = Boolean(cfg.enableNav);

	const slideTotal = thumbsEl.querySelectorAll('.swiper-slide').length;
	if (slideTotal < 1) {
		return;
	}

	const logicalSpvInit = wmfLogicalThumbSpv(slideTotal, cfg);
	const initialThumb = getMinThumbIndex(slideTotal, logicalSpvInit);

	const transitionMs = 400;

	/** @type {import('swiper/types').SwiperOptions} */
	const mainOpts = {
		slidesPerView: 1,
		spaceBetween: 16,
		speed: transitionMs,
		allowTouchMove: false,
		watchOverflow: true,
		loop: false,
	};

	/** @type {import('swiper/types').SwiperOptions} */
	const thumbsOpts = {
		initialSlide: initialThumb,
		slidesPerView: 1,
		spaceBetween: 10,
		speed: transitionMs,
		allowTouchMove: enableNav,
		watchOverflow: true,
		loop: false,
	};

	thumbsOpts.breakpoints = {
		782: {
			slidesPerView: 'auto',
			spaceBetween: 10,
		},
	};

	if (enableNav && root) {
		const prevEl = root.querySelector(`[data-wmf-prev="${cfg.sliderId}"]`);
		const nextEl = root.querySelector(`[data-wmf-next="${cfg.sliderId}"]`);
		thumbsOpts.modules = [Navigation];
		thumbsOpts.navigation = {
			prevEl: prevEl || `[data-wmf-prev="${cfg.sliderId}"]`,
			nextEl: nextEl || `[data-wmf-next="${cfg.sliderId}"]`,
		};
	}

	const mainSwiper = new Swiper(mainEl, mainOpts);
	const thumbsSwiper = new Swiper(thumbsEl, thumbsOpts);
	pairEl._wmfSwipers = { main: mainSwiper, thumbs: thumbsSwiper };

	const applyThumbSlideBounds = () => {
		if (!pairEl._wmfSwipers?.thumbs || pairEl._wmfSwipers.thumbs !== thumbsSwiper || thumbsSwiper.destroyed) {
			return;
		}
		if (!wmfThumbsNeedNav(slideTotal, cfg)) {
			thumbsSwiper.allowSlidePrev = false;
			thumbsSwiper.allowSlideNext = false;
			return;
		}
		const spv = wmfLogicalThumbSpv(slideTotal, cfg);
		const minT = getMinThumbIndex(slideTotal, spv);
		thumbsSwiper.allowSlidePrev = thumbsSwiper.activeIndex > minT;
		thumbsSwiper.allowSlideNext = !thumbsSwiper.isEnd;
	};

	const patchNavOverrides = () => {
		if (!pairEl._wmfSwipers?.thumbs || pairEl._wmfSwipers.thumbs !== thumbsSwiper || thumbsSwiper.destroyed) {
			return;
		}
		thumbsSwiper.navigation?.update?.();
		requestAnimationFrame(() => {
			if (!pairEl._wmfSwipers?.thumbs || pairEl._wmfSwipers.thumbs !== thumbsSwiper || thumbsSwiper.destroyed) {
				return;
			}
			applyThumbSlideBounds();
			updateWmfNavButtons(thumbsSwiper, root, cfg.sliderId, enableNav, slideTotal, cfg);
			toggleNavLocked(root, thumbsSwiper, enableNav, slideTotal, cfg);
		});
	};

	const refreshWmfPair = () => {
		if (!pairEl._wmfSwipers?.thumbs || pairEl._wmfSwipers.thumbs !== thumbsSwiper || thumbsSwiper.destroyed) {
			return;
		}
		syncMainFromThumbs(thumbsSwiper, mainSwiper, 0);
		applyThumbSlideBounds();
		patchNavOverrides();
	};

	thumbsSwiper.on('slideChange', () => {
		wmfPauseVideos(root);
		syncMainFromThumbs(thumbsSwiper, mainSwiper, getWmfTransitionMs(thumbsSwiper));
	});

	thumbsSwiper.on('slideChangeTransitionEnd', () => {
		syncMainFromThumbs(thumbsSwiper, mainSwiper, 0);
		applyThumbSlideBounds();
		patchNavOverrides();
		setTimeout(() => {
			if (!pairEl._wmfSwipers?.thumbs || pairEl._wmfSwipers.thumbs !== thumbsSwiper || thumbsSwiper.destroyed) {
				return;
			}
			updateWmfNavButtons(thumbsSwiper, root, cfg.sliderId, enableNav, slideTotal, cfg);
		}, 0);
	});

	thumbsSwiper.on('breakpoint', () => {
		const spv = wmfLogicalThumbSpv(slideTotal, cfg);
		const minT = getMinThumbIndex(slideTotal, spv);
		if (thumbsSwiper.activeIndex < minT) {
			thumbsSwiper.slideTo(minT, 0);
		}
		syncMainFromThumbs(thumbsSwiper, mainSwiper, 0);
		applyThumbSlideBounds();
		patchNavOverrides();
	});

	thumbsSwiper.on('resize', refreshWmfPair);
	thumbsSwiper.on('observerUpdate', patchNavOverrides);

	mainSwiper.on('resize', () => {
		toggleNavLocked(root, thumbsSwiper, enableNav, slideTotal, cfg);
	});

	queueMicrotask(refreshWmfPair);

	if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
		window.matchMedia('(min-width: 782px)').addEventListener('change', (e) => {
			queueMicrotask(() => {
				if (!pairEl._wmfSwipers?.thumbs) {
					return;
				}
				const { thumbs: thumbsSwiper2 } = pairEl._wmfSwipers;
				const spv = wmfLogicalThumbSpv(slideTotal, cfg);
				const minT = getMinThumbIndex(slideTotal, spv);
				if (thumbsSwiper2.activeIndex < minT) {
					thumbsSwiper2.slideTo(minT, 0);
				} else if (!e.matches && thumbsSwiper2.activeIndex > 0) {
					thumbsSwiper2.slideTo(0, 0);
				}
				thumbsSwiper2.update();
				refreshWmfPair();
			});
		});
	}
}

// —— Видео-карточки ——

/**
 * @param {Element | null} [scope]
 * @param {HTMLVideoElement | null} [excludeVideo]
 */
function wmfPauseVideos(scope, excludeVideo) {
	const container = scope || document;
	container.querySelectorAll('.posts-grid-card--video .posts-grid-card__video').forEach((el) => {
		if (!(el instanceof HTMLVideoElement) || el === excludeVideo) {
			return;
		}
		el.pause();
		const card = el.closest('.posts-grid-card--video');
		if (card) {
			wmfSetVideoPlaying(card, false);
		}
	});
}

/**
 * @param {HTMLElement} card
 * @param {boolean} playing
 */
function wmfSetVideoPlaying(card, playing) {
	const video = card.querySelector('.posts-grid-card__video');
	if (!(video instanceof HTMLVideoElement)) {
		return;
	}
	if (playing) {
		card.classList.add('is-playing');
		video.controls = true;
		video.muted = false;
	} else {
		card.classList.remove('is-playing');
		video.controls = false;
	}
}

const wmfVideoUiBound = new WeakSet();

/**
 * @param {HTMLVideoElement} video
 */
function wmfBindVideoUi(video) {
	if (!(video instanceof HTMLVideoElement) || wmfVideoUiBound.has(video)) {
		return;
	}
	wmfVideoUiBound.add(video);
	const card = video.closest('.posts-grid-card--video');
	if (!card) {
		return;
	}

	video.addEventListener('play', () => {
		wmfPauseVideos(null, video);
		wmfSetVideoPlaying(card, true);
	});
	video.addEventListener('pause', () => {
		wmfSetVideoPlaying(card, false);
	});
	video.addEventListener('ended', () => {
		wmfSetVideoPlaying(card, false);
	});
}

/**
 * @param {Element} root
 */
function initVideoCardsIn(root) {
	root.querySelectorAll('.posts-grid-card--video .posts-grid-card__video').forEach((v) => {
		if (v instanceof HTMLVideoElement) {
			wmfBindVideoUi(v);
		}
	});
}

document.addEventListener(
	'click',
	(event) => {
		const btn = event.target instanceof Element ? event.target.closest('.wp-block-wps-blocks-materials-feed .posts-grid-card__play') : null;
		if (!(btn instanceof HTMLButtonElement)) {
			return;
		}
		const card = btn.closest('.posts-grid-card--video');
		if (!card) {
			return;
		}
		const root = btn.closest('.wp-block-wps-blocks-materials-feed');
		if (!root) {
			return;
		}
		const video = card.querySelector('.posts-grid-card__video');
		if (!(video instanceof HTMLVideoElement)) {
			return;
		}
		event.preventDefault();
		wmfBindVideoUi(video);
		wmfPauseVideos(null, video);
		wmfSetVideoPlaying(card, true);
		video.muted = false;
		video.play().catch(() => {
			wmfSetVideoPlaying(card, false);
		});
	},
	true
);

document.addEventListener(
	'click',
	(event) => {
		const target = event.target;
		const videoEl = target instanceof Element ? target.closest('.wp-block-wps-blocks-materials-feed .posts-grid-card--video.is-playing .posts-grid-card__video') : null;
		if (!(videoEl instanceof HTMLVideoElement)) {
			return;
		}
		if (videoEl.closest('.posts-grid-card__play')) {
			return;
		}
		const card = videoEl.closest('.posts-grid-card--video');
		if (!card?.classList.contains('is-playing')) {
			return;
		}
		const rect = videoEl.getBoundingClientRect();
		const strip = 56;
		if (event instanceof MouseEvent && event.clientY > rect.bottom - strip) {
			return;
		}
		event.preventDefault();
		videoEl.pause();
	},
	true
);

document.addEventListener('click', (event) => {
	const btn = event.target instanceof Element ? event.target.closest('[data-wmf-load-more]') : null;
	if (!(btn instanceof HTMLButtonElement)) {
		return;
	}
	const root = btn.closest('.wp-block-wps-blocks-materials-feed');
	const pairEl = root?.querySelector('[data-wmf-config]');
	if (!(root instanceof HTMLElement) || !(pairEl instanceof HTMLElement)) {
		return;
	}
	if (!isWmfMobileViewport() || !root.classList.contains('wp-block-wps-blocks-materials-feed--is-mobile-list')) {
		return;
	}
	const cfg = parseConfig(pairEl.dataset.wmfConfig);
	if (!cfg || typeof cfg.sliderId !== 'string') {
		return;
	}
	const thumbsEl = pairEl.querySelector(`[data-wmf-thumbs="${cfg.sliderId}"]`);
	const slides = thumbsEl ? [...thumbsEl.querySelectorAll('.swiper-slide')] : [];
	const total = slides.length;
	let visible = pairEl._wmfMobileVisible ?? WMF_MOBILE_INITIAL;
	visible = Math.min(total, visible + WMF_MOBILE_STEP);
	pairEl._wmfMobileVisible = visible;
	slides.forEach((slide, i) => {
		slide.classList.toggle('is-wmf-mobile-hidden', i >= visible);
	});
	btn.hidden = total <= WMF_MOBILE_INITIAL || visible >= total;
	initVideoCardsIn(root);
});

document.addEventListener('click', (event) => {
	const btn = event.target instanceof Element ? event.target.closest('.wp-block-wps-blocks-materials-feed__filter[data-wmf-filter]') : null;
	if (!(btn instanceof HTMLButtonElement)) {
		return;
	}
	const root = btn.closest('.wp-block-wps-blocks-materials-feed');
	if (!root) {
		return;
	}
	const filter = btn.getAttribute('data-wmf-filter') || 'all';

	if (applyFilterToSingle(root, filter)) {
		updateFilterTabs(root, filter);
		return;
	}

	const pairEl = root.querySelector('[data-wmf-config]');
	if (pairEl instanceof HTMLElement) {
		rebuildWmfSliders(pairEl, root, filter);
		updateFilterTabs(root, filter);
	}
});

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.wp-block-wps-blocks-materials-feed [data-wmf-config]').forEach((pair) => {
		if (!(pair instanceof HTMLElement)) {
			return;
		}
		const cfg = parseConfig(pair.dataset.wmfConfig);
		if (!cfg || typeof cfg.sliderId !== 'string') {
			return;
		}
		const mainEl = pair.querySelector(`[data-wmf-main="${cfg.sliderId}"]`);
		const thumbsEl = pair.querySelector(`[data-wmf-thumbs="${cfg.sliderId}"]`);
		if (mainEl instanceof HTMLElement && thumbsEl instanceof HTMLElement) {
			cacheWmfSlideRows(pair, mainEl, thumbsEl);
		}
		const root = pair.closest('.wp-block-wps-blocks-materials-feed');
		if (root instanceof HTMLElement) {
			syncWmfBlockLayout(root);
		}
	});
	document.querySelectorAll('.wp-block-wps-blocks-materials-feed').forEach((root) => initVideoCardsIn(root));
	if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
		window.matchMedia(`(max-width: ${WMF_MOBILE_MAX}px)`).addEventListener('change', () => {
			document.querySelectorAll('.wp-block-wps-blocks-materials-feed').forEach((root) => {
				if (root instanceof HTMLElement) {
					syncWmfBlockLayout(root);
				}
			});
		});
	}
});
